import { useState, useCallback } from 'react';
import { useLocalStorage } from './useLocalStorage';
import logger from '../services/logger';

/**
 * Custom hook for cart management
 * Separates cart business logic from presentation components
 */
export const useCart = () => {
  const [cartItems, setCartItems] = useLocalStorage('cart', []);
  const [isCartOpen, setIsCartOpen] = useState(false);

  // Add item to cart
  const addToCart = useCallback(
    item => {
      setCartItems(prevCart => {
        const existingItem = prevCart.find(cartItem => cartItem.id === item.id);

        if (existingItem) {
          // Log quantity update
          logger.logEcommerceEvent('cart_item_quantity_updated', {
            itemId: item.id,
            itemName: item.name,
            oldQuantity: existingItem.quantity,
            newQuantity: existingItem.quantity + 1,
            price: item.price,
          });

          return prevCart.map(cartItem =>
            cartItem.id === item.id ? { ...cartItem, quantity: cartItem.quantity + 1 } : cartItem,
          );
        }

        // Log new item added
        logger.logEcommerceEvent('item_added_to_cart', {
          itemId: item.id,
          itemName: item.name,
          price: item.price,
          category: item.category,
          quantity: 1,
        });

        return [...prevCart, { ...item, quantity: 1 }];
      });

      // Open cart after adding item
      setIsCartOpen(true);
    },
    [setCartItems],
  );

  // Add multiple items to cart (for custom outfits)
  const addMultipleToCart = useCallback(
    items => {
      if (!items || items.length === 0) {
        return;
      }

      // Log custom outfit added to cart
      logger.logEcommerceEvent('custom_outfit_added_to_cart', {
        itemCount: items.length,
        totalValue: items.reduce((sum, item) => sum + item.price * item.quantity, 0),
        items: items.map(item => ({
          id: item.id,
          name: item.name,
          quantity: item.quantity,
          price: item.price,
        })),
      });

      setCartItems(prevCart => {
        const newCart = [...prevCart];

        items.forEach(item => {
          const existingIndex = newCart.findIndex(
            cartItem =>
              cartItem.id === item.id &&
              cartItem.size === item.size &&
              cartItem.color === item.color,
          );

          if (existingIndex >= 0) {
            // Item exists, update quantity
            newCart[existingIndex].quantity += item.quantity;
          } else {
            // Add new item
            newCart.push(item);
          }
        });

        return newCart;
      });

      // Open cart after adding items
      setIsCartOpen(true);
    },
    [setCartItems],
  );

  // Update item quantity
  const updateQuantity = useCallback(
    (itemId, newQuantity) => {
      setCartItems(prevCart => {
        const item = prevCart.find(item => item.id === itemId);

        if (item) {
          if (newQuantity <= 0) {
            // Log item removal
            logger.logEcommerceEvent('item_removed_from_cart', {
              itemId: item.id,
              itemName: item.name,
              price: item.price,
              removedQuantity: item.quantity,
            });
            return prevCart.filter(item => item.id !== itemId);
          } else {
            // Log quantity update
            logger.logEcommerceEvent('cart_item_quantity_updated', {
              itemId: item.id,
              itemName: item.name,
              oldQuantity: item.quantity,
              newQuantity: newQuantity,
              price: item.price,
            });
            return prevCart.map(item =>
              item.id === itemId ? { ...item, quantity: newQuantity } : item,
            );
          }
        }
        return prevCart;
      });
    },
    [setCartItems],
  );

  // Remove item from cart
  const removeFromCart = useCallback(
    itemId => {
      updateQuantity(itemId, 0);
    },
    [updateQuantity],
  );

  // Clear cart
  const clearCart = useCallback(() => {
    logger.logEcommerceEvent('cart_cleared', {
      itemCount: cartItems.length,
      totalValue: cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0),
    });
    setCartItems([]);
  }, [cartItems, setCartItems]);

  // Toggle cart sidebar
  const toggleCart = useCallback(() => {
    setIsCartOpen(prev => !prev);
  }, []);

  // Close cart
  const closeCart = useCallback(() => {
    setIsCartOpen(false);
  }, []);

  // Open cart
  const openCart = useCallback(() => {
    setIsCartOpen(true);
  }, []);

  // Calculate cart totals
  const cartTotal = cartItems.reduce((total, item) => total + item.price * item.quantity, 0);
  const cartItemCount = cartItems.reduce((count, item) => count + item.quantity, 0);

  // Save item for later (move to wishlist)
  const saveForLater = useCallback(
    item => {
      logger.logEcommerceEvent('item_saved_for_later', {
        itemId: item.id,
        itemName: item.name,
        price: item.price,
        fromCart: true,
      });

      // Remove from cart after saving for later
      removeFromCart(item.id);
    },
    [removeFromCart],
  );

  // Handle checkout
  const handleCheckout = useCallback(() => {
    if (cartItems.length === 0) {
      logger.logEcommerceEvent('checkout_attempted_empty_cart', {});
      return false;
    }

    // Log checkout initiation
    logger.logEcommerceEvent('checkout_initiated', {
      itemCount: cartItems.length,
      totalValue: cartTotal,
      items: cartItems.map(item => ({
        id: item.id,
        name: item.name,
        quantity: item.quantity,
        price: item.price,
      })),
    });

    return true;
  }, [cartItems, cartTotal]);

  return {
    // State
    cartItems,
    isCartOpen,
    cartTotal,
    cartItemCount,

    // Actions
    addToCart,
    addMultipleToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    toggleCart,
    closeCart,
    openCart,
    handleCheckout,
    saveForLater,
  };
};

export default useCart;
